import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../lib/apiClient';
import { BounceEmailRecord, BounceReason, BounceType } from '../types';

type ApiBounceEmailPayload = {
  id?: number;
  subject?: string;
  finalRecipient?: string;
  action?: string;
  bounceCode?: string;
  bounceReason?: string;
  bounceType?: string;
  bouncedAt?: string;
  createdAt?: string;
};

type BounceEmailLogsResponse = {
  data: BounceEmailRecord[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
};

type ApiBounceEmailMeta = {
  total?: number;
  page?: number;
  limit?: number;
  totalPages?: number;
};

const extractBounceEmailLogs = (payload: unknown): ApiBounceEmailPayload[] => {
  if (Array.isArray(payload)) return payload as ApiBounceEmailPayload[];

  if (payload && typeof payload === 'object' && Array.isArray((payload as { data?: unknown }).data)) {
    return (payload as { data: ApiBounceEmailPayload[] }).data;
  }

  return [];
};

const normalizeBounceType = (value?: string): BounceType => {
  const normalizedValue = value?.toUpperCase();
  if (normalizedValue === 'HARD_BOUNCE') return 'HARD_BOUNCE';
  if (normalizedValue === 'SOFT_BOUNCE') return 'SOFT_BOUNCE';
  return 'UNKNOWN';
};

const normalizeBounceReason = (value?: string): BounceReason => {
  const normalizedValue = value?.toLowerCase();
  if (normalizedValue === 'temporary_failure') return 'TEMPORARY_FAILURE';
  if (normalizedValue === 'invalid_recipient') return 'INVALID_RECIPIENT';
  return 'UNCLASSIFIED';
};

const mapApiBounceEmail = (item: ApiBounceEmailPayload, index: number): BounceEmailRecord => ({
  id: item.id ?? index + 1,
  finalRecipient: item.finalRecipient ?? 'unknown@example.com',
  subject: item.subject ?? '-',
  action: item.action ?? '-',
  bounceType: normalizeBounceType(item.bounceType),
  bounceCode: item.bounceCode ?? '-',
  bounceReason: normalizeBounceReason(item.bounceReason),
  bouncedAt: item.bouncedAt ?? '',
  createdAt: item.createdAt ?? '',
});

const fetchBounceEmailLogs = async (
  page: number,
  limit: number,
  searchTerm: string,
  sortField: string,
  sortDirection: 'asc' | 'desc'
): Promise<BounceEmailLogsResponse> => {
  const normalizedSearchTerm = searchTerm.trim();
  const searchParam = normalizedSearchTerm.length > 0 ? `&search=${encodeURIComponent(normalizedSearchTerm)}` : '';
  const sortFieldParam = sortField.trim().length > 0 ? `&sortBy=${encodeURIComponent(sortField)}` : '';
  const sortDirectionParam = sortField.trim().length > 0 ? `&sortDirection=${encodeURIComponent(sortDirection)}` : '';
  const responseData = await apiClient.getJson<unknown>(
    `/bounce-email/logs?page=${page}&limit=${limit}${searchParam}${sortFieldParam}${sortDirectionParam}`
  );
  const mappedData = extractBounceEmailLogs(responseData).map(mapApiBounceEmail);
  const rawMeta =
    responseData && typeof responseData === 'object' ? ((responseData as { meta?: ApiBounceEmailMeta }).meta ?? {}) : {};

  return {
    data: mappedData,
    meta: {
      total: rawMeta.total ?? mappedData.length,
      page: rawMeta.page ?? page,
      limit: rawMeta.limit ?? limit,
      totalPages: rawMeta.totalPages ?? 1,
    },
  };
};

export const useBounceEmailLogsQuery = (
  enabled: boolean,
  page: number,
  limit: number,
  searchTerm = '',
  sortField = '',
  sortDirection: 'asc' | 'desc' = 'asc'
) =>
  useQuery({
    queryKey: ['bounce-email-logs', page, limit, searchTerm, sortField, sortDirection],
    queryFn: () => fetchBounceEmailLogs(page, limit, searchTerm, sortField, sortDirection),
    enabled,
  });
